import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, List, CalendarDays, ChevronLeft, ChevronRight, CalendarClock, Users, Clock, MapPin, Pencil, Trash2, Copy, CopyPlus, CheckCircle2, RefreshCw, Ban, AlertTriangle } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { StatCard } from "@/components/common/StatCard";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { PERSONS, LOCATIONS, OFFICE_STATUS, WORKSTREAMS, personMeta, officeStatusBadge, mondayOf, todayIso, weekDays, addDays, rangeLabel, dowLabel, dayNum, durH, fmtHours, fmtRange, findOverlaps, findDuplicate, sharedOfficeByDate } from "@/data/officeOptions";

const PersonChip = ({ person, size = "sm" }) => {
  const m = personMeta(person);
  const s = size === "sm" ? "h-5 min-w-5 px-1 text-[10px]" : "h-6 min-w-6 px-1.5 text-[11px]";
  return <span className={`inline-flex items-center justify-center rounded-full font-bold text-white ${s}`} style={{ backgroundColor: m.color }}>{m.initials}</span>;
};

const blankEntry = (user) => ({
  person: user?.name === "Richard" ? "Richard" : "Steven",
  date: todayIso(), start_time: "09:00", end_time: "17:00", location: "Office",
  working_focus: "", workstream: "Operations", notes: "", status: "Planned",
  created_by: user?.name || "",
});

export const OfficeSchedulePanel = ({ initialPerson, sharedOnly, openEntryId, onConsume, onClearFilter }) => {
  const { currentUser, canWrite } = useUser();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [monday, setMonday] = useState(mondayOf(todayIso()));
  const [view, setView] = useState(initialPerson || sharedOnly ? "list" : "calendar");
  const [form, setForm] = useState(null); const [formOpen, setFormOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [warn, setWarn] = useState(null);      // {messages, onProceed}
  const [copyOpen, setCopyOpen] = useState(false); const [exclude, setExclude] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async (mon) => {
    const [all, s] = await Promise.all([api.list("office-schedule"), api.officeSummary(mon)]);
    setEntries(all); setSummary(s); setLoaded(true);
  }, []);
  useEffect(() => { load(monday); }, [load, monday]);

  // Direct entry deep link: /?tab=office-schedule&entry=<id>
  useEffect(() => {
    if (!openEntryId || !loaded) return;
    const e = entries.find((x) => x.id === openEntryId);
    if (e) { setMonday(mondayOf(e.date)); setDetail(e); }
    else toast.error("That schedule entry is unavailable — it may have been deleted.");
    onConsume?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEntryId, loaded]);

  const dlLabel = sharedOnly ? "shared office hours" : initialPerson ? `${initialPerson}'s sessions` : null;
  const applyDeep = (list) => {
    let r = list;
    if (initialPerson) r = r.filter((e) => e.person === initialPerson);
    if (sharedOnly) { const sm = sharedOfficeByDate(r); r = r.filter((e) => sm[e.date]); }
    return r;
  };

  const days = weekDays(monday);
  const weekEntries = useMemo(() => entries.filter((e) => e.date >= monday && e.date <= addDays(monday, 6)), [entries, monday]);
  const sharedMap = useMemo(() => sharedOfficeByDate(weekEntries), [weekEntries]);

  const openAdd = (date) => { setForm({ ...blankEntry(currentUser), date: date || todayIso() }); setFormOpen(true); };
  const openEdit = (e) => { setDetail(null); setForm({ ...e }); setFormOpen(true); };
  const openDuplicate = (e) => { setDetail(null); const { id, created_at, updated_at, ...rest } = e; setForm({ ...rest, status: "Planned", date: addDays(e.date, 1), created_by: currentUser?.name || "" }); setFormOpen(true); };

  const persist = async (payload) => {
    try {
      if (payload.id) { const u = await api.update("office-schedule", payload.id, payload); toast.success("Entry updated"); if (detail) setDetail(u); }
      else { await api.create("office-schedule", payload); toast.success("Entry added"); }
      setFormOpen(false); setForm(null); await load(monday);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save entry"); }
  };

  const submitForm = () => {
    const f = form;
    if (!f.person || !f.date || !f.start_time || !f.end_time || !f.location) { toast.error("Person, date, start, end and location are required"); return; }
    if (durH(f.start_time, f.end_time) <= 0) { toast.error("End time must be later than start time"); return; }
    const overlaps = findOverlaps(f, entries);
    const dup = findDuplicate(f, entries);
    const messages = [];
    if (dup) messages.push(`An identical entry already exists for ${f.person} on ${f.date}.`);
    overlaps.forEach((o) => messages.push(`${o.person} already has ${fmtRange(o.start_time, o.end_time)} (${o.location}) on ${o.date}.`));
    if (messages.length) setWarn({ messages, onProceed: () => { setWarn(null); persist(f); } });
    else persist(f);
  };

  const doDelete = async (reason) => { await api.remove("office-schedule", confirmId, reason); toast.success("Moved to Recycle Bin"); setConfirmId(null); setDetail(null); await load(monday); };
  const transition = async (e, status) => { const u = await api.officeTransition(e.id, status); toast.success(`Marked ${status}`); setDetail(u); await load(monday); };

  const openCopy = () => { setExclude(new Set()); setCopyOpen(true); };
  const copySources = weekEntries.filter((e) => e.status !== "Cancelled");
  const doCopyWeek = async () => {
    const res = await api.officeCopyWeek(monday, [...exclude]);
    toast.success(`Copied ${res.copied} entr${res.copied === 1 ? "y" : "ies"} to next week`);
    setCopyOpen(false); setMonday(addDays(monday, 7));
  };

  const cards = [
    { label: "Steven · Planned Hours", value: summary ? fmtHours(summary.steven_hours) : "—", icon: Clock, tone: "royal" },
    { label: "Richard · Planned Hours", value: summary ? fmtHours(summary.richard_hours) : "—", icon: Clock, tone: "teal" },
    { label: "Shared Office Hours", value: summary ? fmtHours(summary.shared_office_hours) : "—", icon: Users, tone: "orange" },
    { label: "Steven Office Days", value: summary?.steven_office_days ?? "—", icon: CalendarDays, tone: "cyan" },
    { label: "Richard Office Days", value: summary?.richard_office_days ?? "—", icon: CalendarDays, tone: "aqua" },
    { label: "Both In Office", value: summary?.both_office_days ?? "—", icon: Users, tone: "gold", hint: summary?.both_office_days ? "days" : "" },
  ];

  const columns = [
    { key: "date", header: "Date", render: (r) => <span className="whitespace-nowrap font-medium text-[#14245F]">{dowLabel(r.date)} {r.date}</span> },
    { key: "person", header: "Person", render: (r) => <span className="inline-flex items-center gap-1.5"><PersonChip person={r.person} /> {r.person}</span> },
    { key: "time", header: "Time", render: (r) => <span className="whitespace-nowrap text-[#14245F]">{fmtRange(r.start_time, r.end_time)}</span> },
    { key: "location", header: "Location" },
    { key: "working_focus", header: "Working focus", render: (r) => r.working_focus || <span className="text-[#94A3C4]">—</span> },
    { key: "workstream", header: "Workstream" },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={officeStatusBadge(r.status)} label={r.status} /> },
    { key: "_a", header: "", render: (r) => <button data-testid={`open-office-${r.id}`} onClick={() => setDetail(r)} className="text-sm font-semibold text-[#1D4ED8] hover:underline">Open</button> },
  ];
  const filters = [
    { key: "person", label: "Person", options: PERSONS.map((v) => ({ value: v, label: v })) },
    { key: "location", label: "Location", options: LOCATIONS.map((v) => ({ value: v, label: v })) },
    { key: "workstream", label: "Workstream", options: WORKSTREAMS.map((v) => ({ value: v, label: v })) },
    { key: "status", label: "Status", options: OFFICE_STATUS.map((v) => ({ value: v, label: v })) },
  ];

  return (
    <SectionCard testid="office-schedule-card" accent="#1D4ED8" icon={CalendarDays} title="Office Schedule"
      subtitle="Plan when Steven and Richard are working — see where the founders overlap in the office."
      action={
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-[#E4E9F3] p-0.5">
            {[["calendar", CalendarDays], ["list", List]].map(([v, Icon]) => (
              <button key={v} data-testid={`office-view-${v}`} onClick={() => setView(v)} className={`flex h-7 w-7 items-center justify-center rounded-md ${view === v ? "bg-[#1D4ED8] text-white" : "text-[#5A6B8C]"}`}><Icon size={15} /></button>
            ))}
          </div>
          {canWrite && <Button size="sm" data-testid="office-copy-week-btn" variant="outline" onClick={openCopy} className="border-[#E4E9F3] text-[#1D4ED8]"><CopyPlus size={15} /> Copy week</Button>}
          {canWrite && <Button size="sm" data-testid="add-office-btn" onClick={() => openAdd()} className="bg-[#1D4ED8] text-white hover:bg-[#1A45C0]"><Plus size={15} /> Add entry</Button>}
        </div>
      }>

      {/* Week navigation */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" data-testid="office-prev-week" onClick={() => setMonday(addDays(monday, -7))} className="h-9 border-[#E4E9F3]"><ChevronLeft size={16} /></Button>
          <Button size="sm" variant="outline" data-testid="office-this-week" onClick={() => setMonday(mondayOf(todayIso()))} className="h-9 border-[#E4E9F3] text-[#1D4ED8]">This week</Button>
          <Button size="sm" variant="outline" data-testid="office-next-week" onClick={() => setMonday(addDays(monday, 7))} className="h-9 border-[#E4E9F3]"><ChevronRight size={16} /></Button>
          <div className="ml-2 flex items-center gap-2">
            <CalendarClock size={16} className="text-[#5A6B8C]" />
            <span data-testid="office-week-range" className="font-heading text-base font-semibold text-[#14245F]">{rangeLabel(monday)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="jump" className="text-xs text-[#5A6B8C]">Jump to</Label>
          <Input id="jump" type="date" data-testid="office-jump-date" className="h-9 w-40 border-[#E4E9F3]" onChange={(e) => e.target.value && setMonday(mondayOf(e.target.value))} />
        </div>
      </div>

      {/* Weekly summary cards */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="office-summary">
        {cards.map((c, i) => <StatCard key={c.label} index={i} testid={`office-stat-${i}`} {...c} />)}
      </div>

      {dlLabel && (
        <div data-testid="office-filter-banner" className="mb-4 flex items-center justify-between rounded-xl bg-[#1D4ED8]/10 px-3 py-2 text-xs font-semibold text-[#1D4ED8]">
          <span data-testid="office-filter-label">Filtered to {dlLabel} · {applyDeep(entries).length} matching</span>
          <button data-testid="clear-office-filter" onClick={() => onClearFilter?.()} className="inline-flex items-center gap-1 underline hover:no-underline">Clear filter</button>
        </div>
      )}

      {view === "calendar" ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-7" data-testid="office-calendar">
          {days.map((d) => {
            const dayEntries = weekEntries.filter((e) => e.date === d).sort((a, b) => a.start_time.localeCompare(b.start_time));
            const isToday = d === todayIso();
            const shared = sharedMap[d];
            return (
              <div key={d} data-testid={`office-day-${d}`} className={`rounded-xl border p-2.5 ${isToday ? "border-[#1D4ED8] bg-[#F5F8FF]" : "border-[#E4E9F3] bg-white"}`}>
                <div className="mb-2 flex items-center justify-between">
                  <div><p className="text-[11px] font-bold uppercase tracking-wide text-[#5A6B8C]">{dowLabel(d)}</p><p className="font-heading text-lg font-bold leading-none text-[#14245F]">{dayNum(d)}</p></div>
                  {canWrite && <button data-testid={`office-add-day-${d}`} onClick={() => openAdd(d)} className="flex h-6 w-6 items-center justify-center rounded-md text-[#1D4ED8] hover:bg-[#1D4ED8]/10"><Plus size={14} /></button>}
                </div>
                {shared && (
                  <div data-testid={`office-shared-${d}`} className="mb-2 flex items-center gap-1.5 rounded-lg bg-[#F97316]/12 px-2 py-1 text-[10px] font-bold text-[#C2410C]">
                    <Users size={11} /> Both founders · {fmtHours(shared)} shared
                  </div>
                )}
                <div className="space-y-2">
                  {dayEntries.length === 0 ? <p className="py-3 text-center text-[11px] text-[#B7C1DA]">—</p> : dayEntries.map((e) => {
                    const m = personMeta(e.person); const cancelled = e.status === "Cancelled";
                    return (
                      <button key={e.id} data-testid={`office-entry-${e.id}`} onClick={() => setDetail(e)}
                        className={`w-full rounded-lg border-l-[3px] p-2 text-left transition-colors hover:shadow-sm ${cancelled ? "opacity-55" : ""}`}
                        style={{ borderLeftColor: m.color, backgroundColor: m.tint }}>
                        <div className="flex items-center justify-between gap-1">
                          <span className="inline-flex items-center gap-1"><PersonChip person={e.person} /><span className="text-[11px] font-semibold text-[#14245F]">{e.person}</span></span>
                          <StatusBadge status={officeStatusBadge(e.status)} label={e.status} />
                        </div>
                        <p className={`mt-1 text-[11px] font-medium text-[#14245F] ${cancelled ? "line-through" : ""}`}>{fmtRange(e.start_time, e.end_time)}</p>
                        <p className="flex items-center gap-1 text-[10px] text-[#5A6B8C]"><MapPin size={10} /> {e.location}</p>
                        {e.working_focus && <p className="mt-0.5 truncate text-[10px] text-[#5A6B8C]">{e.working_focus}</p>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        weekEntries.length === 0 && entries.length === 0
          ? <EmptyState image="/commando-mascot-v2.webp" icon={CalendarDays} title="No schedule entries yet" description="Add the first office session for this week." actionLabel={canWrite ? "Add entry" : undefined} onAction={canWrite ? () => openAdd() : undefined} />
          : <DataTable testid="office-table" columns={columns} rows={applyDeep(entries)} searchKeys={["working_focus", "notes", "workstream", "person"]} searchPlaceholder="Search focus, notes…" filters={filters} />
      )}

      {/* Entry form */}
      <Dialog open={formOpen} onOpenChange={(o) => { if (!o) { setFormOpen(false); setForm(null); } }}>
        <DialogContent data-testid="office-form" className="max-h-[92vh] max-w-lg overflow-y-auto border-[#E4E9F3] bg-white">
          <DialogHeader><DialogTitle className="font-heading text-xl text-[#14245F]">{form?.id ? "Edit Office Entry" : "Add Office Entry"}</DialogTitle><DialogDescription className="text-[#5A6B8C]">Log where a founder is working for this session.</DialogDescription></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Person *"><Select value={form.person} onValueChange={(v) => setForm({ ...form, person: v })}><SelectTrigger data-testid="office-field-person" className="border-[#E4E9F3]"><SelectValue /></SelectTrigger><SelectContent>{PERSONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></Field>
                <Field label="Date *"><Input type="date" data-testid="office-field-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="border-[#E4E9F3]" /></Field>
                <Field label="Start time *"><Input type="time" data-testid="office-field-start" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="border-[#E4E9F3]" /></Field>
                <Field label="End time *"><Input type="time" data-testid="office-field-end" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="border-[#E4E9F3]" /></Field>
                <Field label="Location *"><Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}><SelectTrigger data-testid="office-field-location" className="border-[#E4E9F3]"><SelectValue /></SelectTrigger><SelectContent>{LOCATIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></Field>
                <Field label="Workstream"><Select value={form.workstream} onValueChange={(v) => setForm({ ...form, workstream: v })}><SelectTrigger data-testid="office-field-workstream" className="border-[#E4E9F3]"><SelectValue /></SelectTrigger><SelectContent>{WORKSTREAMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></Field>
              </div>
              <Field label="Working focus"><Input data-testid="office-field-focus" value={form.working_focus} onChange={(e) => setForm({ ...form, working_focus: e.target.value })} placeholder="e.g. Module 3 content review" className="border-[#E4E9F3]" /></Field>
              <Field label="Status"><Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="office-field-status" className="border-[#E4E9F3]"><SelectValue /></SelectTrigger><SelectContent>{OFFICE_STATUS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Notes"><Textarea data-testid="office-field-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-[#E4E9F3]" rows={2} /></Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setFormOpen(false); setForm(null); }} className="border-[#E4E9F3]">Cancel</Button>
            <Button data-testid="office-form-submit" onClick={submitForm} className="bg-[#1D4ED8] text-white hover:bg-[#1A45C0]">{form?.id ? "Save changes" : "Add entry"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Overlap / duplicate warning */}
      <Dialog open={!!warn} onOpenChange={(o) => !o && setWarn(null)}>
        <DialogContent data-testid="office-warn-dialog" className="max-w-md border-[#E4E9F3] bg-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading text-lg text-[#C2410C]"><AlertTriangle size={18} /> Possible clash</DialogTitle>
            <DialogDescription className="text-[#5A6B8C]">Review before saving:</DialogDescription></DialogHeader>
          <ul className="space-y-1.5 text-sm text-[#14245F]">{warn?.messages.map((m, i) => <li key={i} className="rounded-lg bg-[#F97316]/10 px-3 py-2">{m}</li>)}</ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWarn(null)} className="border-[#E4E9F3]">Go back</Button>
            <Button data-testid="office-warn-proceed" onClick={() => warn.onProceed()} className="bg-[#F97316] text-white hover:bg-[#EA6A0E]">Save anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy week preview */}
      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent data-testid="office-copy-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto border-[#E4E9F3] bg-white">
          <DialogHeader><DialogTitle className="font-heading text-xl text-[#14245F]">Copy week forward</DialogTitle>
            <DialogDescription className="text-[#5A6B8C]">Copies to <strong>{rangeLabel(addDays(monday, 7))}</strong> as Planned. Cancelled entries are skipped. Untick any you don't want.</DialogDescription></DialogHeader>
          {copySources.length === 0 ? <EmptyState icon={Copy} title="Nothing to copy" description="This week has no active entries." /> : (
            <ul className="space-y-2">{copySources.map((e) => (
              <li key={e.id} className="flex items-center gap-3 rounded-lg border border-[#E4E9F3] px-3 py-2">
                <Checkbox data-testid={`copy-include-${e.id}`} checked={!exclude.has(e.id)} onCheckedChange={(c) => { const n = new Set(exclude); if (c) n.delete(e.id); else n.add(e.id); setExclude(n); }} />
                <PersonChip person={e.person} />
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-[#14245F]">{dowLabel(e.date)} · {fmtRange(e.start_time, e.end_time)} · {e.location}</p>{e.working_focus && <p className="truncate text-xs text-[#5A6B8C]">{e.working_focus}</p>}</div>
              </li>
            ))}</ul>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyOpen(false)} className="border-[#E4E9F3]">Cancel</Button>
            <Button data-testid="office-copy-confirm" disabled={copySources.length === exclude.size} onClick={doCopyWeek} className="bg-[#1D4ED8] text-white hover:bg-[#1A45C0]">Copy {copySources.length - exclude.size} to next week</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="office-detail" className="max-w-md border-[#E4E9F3] bg-white">
          {detail && (<>
            <DialogHeader>
              <div className="flex items-center gap-2"><PersonChip person={detail.person} size="lg" /><StatusBadge status={officeStatusBadge(detail.status)} label={detail.status} /></div>
              <DialogTitle className="mt-1 font-heading text-xl text-[#14245F]">{detail.person} · {dowLabel(detail.date)} {detail.date}</DialogTitle>
              <DialogDescription className="text-[#5A6B8C]">{fmtRange(detail.start_time, detail.end_time)} · {fmtHours(durH(detail.start_time, detail.end_time))} · {detail.location}</DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5 text-sm text-[#14245F]">
              {detail.working_focus && <p><span className="text-[#5A6B8C]">Focus: </span>{detail.working_focus}</p>}
              <p><span className="text-[#5A6B8C]">Workstream: </span>{detail.workstream}</p>
              {detail.notes && <p><span className="text-[#5A6B8C]">Notes: </span>{detail.notes}</p>}
              {detail.created_by && <p className="text-xs text-[#94A3C4]">Added by {detail.created_by}</p>}
            </div>
            {canWrite && (
              <div className="mt-3 flex flex-wrap gap-2 border-t border-[#E4E9F3] pt-3">
                <Button size="sm" data-testid="office-edit" onClick={() => openEdit(detail)} className="bg-[#1D4ED8] text-white hover:bg-[#1A45C0]"><Pencil size={14} /> Edit</Button>
                <Button size="sm" variant="outline" data-testid="office-duplicate" onClick={() => openDuplicate(detail)} className="border-[#E4E9F3]"><Copy size={14} /> Duplicate</Button>
                {detail.status !== "Confirmed" && <Button size="sm" variant="outline" data-testid="office-confirm" onClick={() => transition(detail, "Confirmed")} className="border-[#0D9488]/40 text-[#0F766E]"><CheckCircle2 size={14} /> Confirm</Button>}
                {detail.status !== "Changed" && <Button size="sm" variant="outline" data-testid="office-changed" onClick={() => transition(detail, "Changed")} className="border-[#E0A82E]/50 text-[#A16207]"><RefreshCw size={14} /> Mark changed</Button>}
                {detail.status !== "Cancelled" && <Button size="sm" variant="outline" data-testid="office-cancel" onClick={() => transition(detail, "Cancelled")} className="border-[#E4E9F3] text-[#5A6B8C]"><Ban size={14} /> Cancel</Button>}
                <Button size="sm" variant="outline" data-testid="office-delete" onClick={() => setConfirmId(detail.id)} className="border-[#E4E9F3] text-[#B91C1C] hover:bg-[#DC2626]/10"><Trash2 size={14} /> Delete</Button>
              </div>
            )}
          </>)}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)} title="Delete this schedule entry?" onConfirm={doDelete} softDelete />
    </SectionCard>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1"><Label className="text-xs font-medium text-[#5A6B8C]">{label}</Label>{children}</div>
);
